"use client"

import { createContext, ReactNode, useContext, useLayoutEffect, useRef, useState } from "react"

interface ScrollContextType {
  isScrolled: boolean
}

const ScrollContext = createContext<ScrollContextType>({ isScrolled: false })

export const useScroll = () => {
  const context = useContext(ScrollContext)
  if (!context) {
    throw new Error("useScroll must be used within a ScrollProvider")
  }
  return context
}

interface ScrollProviderProps {
  children: ReactNode
}

/**
 * 滚动状态提供者
 * 监听页面滚动，提供滚动状态给子组件
 * 兼容 React 19 的客户端组件规范，解决 SSR 水合闪烁问题
 */
export function ScrollProvider({ children }: ScrollProviderProps) {
  // 服务端渲染时始终返回 false，确保一致性
  const [isScrolled, setIsScrolled] = useState(false)
  const tickingRef = useRef(false)

  const handleScroll = () => {
    if (!tickingRef.current) {
      requestAnimationFrame(() => {
        const scrollTop = window.scrollY
        // 增加迟滞逻辑，防止在阈值附近反复切换
        setIsScrolled((prev) => {
          // 向下滚动超过 10px 切换为 scrolled
          if (scrollTop > 10) return true
          // 向上滚动回到顶部 (<= 2px) 才切换回 transparent
          // 保持一个缓冲区，避免临界值抖动
          if (scrollTop <= 2) return false
          return prev
        })
        tickingRef.current = false
      })
      tickingRef.current = true
    }
  }

  useLayoutEffect(() => {
    handleScroll()
    // 监听滚动事件（使用节流优化性能）
    window.addEventListener("scroll", handleScroll, { passive: true })

    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  return <ScrollContext.Provider value={{ isScrolled }}>{children}</ScrollContext.Provider>
}
